import { Group, RemoveScroll, Stack, Text } from '@mantine/core';
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type FC,
    type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import classes from './ExplainWindow.module.css';

type Props = {
    opened: boolean;
    title: string;
    subtitle?: string | null;
    onClose: () => void;
    /** Widen for windows that hold a table rather than a paragraph. */
    wide?: boolean;
    /**
     * The window's verdict, pinned under the titlebar and OUTSIDE the scrollport.
     *
     * The one number the window exists to answer must not scroll away: it is the thing every
     * edit below is aimed at, and reading it means scrolling back up past the table you were
     * editing. Pinned, the tables move under an answer that stays put.
     */
    banner?: ReactNode;
    /** Pinned action bar, rendered OUTSIDE the scrollport so it never covers content. */
    footer?: ReactNode;
    children: ReactNode;
};

/**
 * A floating "why does this row say that?" window.
 *
 * Portalled to document.body so it escapes the grid's stacking/scroll context — a drawer
 * anchored inside DataSheet gets clipped by the sticky header. Closes on backdrop click and
 * on Escape, because it is a read-and-dismiss answer, not a form.
 */
const ExplainWindow: FC<Props> = ({
    opened,
    title,
    subtitle,
    onClose,
    wide = false,
    banner,
    footer,
    children,
}) => {
    useEffect(() => {
        if (!opened) return;
        const onKey = (e: KeyboardEvent): void => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [opened, onClose]);

    /**
     * Edge fades, because the scrollbar cannot be relied on to say there is more.
     *
     * macOS uses overlay scrollbars: they appear only while scrolling and then vanish, and no
     * CSS can pin them open — `scrollbar-gutter: stable` reserves nothing for an overlay bar,
     * and `::-webkit-scrollbar` is ignored once `scrollbar-width` is set. So a sheet whose
     * content is clipped mid-row looks like a table that was simply truncated. A fade at the
     * live edge is the cue that survives every OS scrollbar setting.
     */
    const bodyRef = useRef<HTMLDivElement>(null);
    const [edges, setEdges] = useState({ top: false, bottom: false });

    const measure = useCallback((): void => {
        const el = bodyRef.current;
        if (!el) return;
        const hidden = el.scrollHeight - el.clientHeight;
        setEdges({
            top: el.scrollTop > 4,
            bottom: hidden > 4 && el.scrollTop < hidden - 4,
        });
    }, []);

    useLayoutEffect(() => {
        const el = bodyRef.current;
        if (!opened || !el) return;
        measure();
        // Content grows and shrinks under the user (open a drill-down, "show all"), so the
        // cue has to track the box, not just scroll events.
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        for (const child of Array.from(el.children)) ro.observe(child);
        return () => ro.disconnect();
    }, [opened, children, measure]);

    if (!opened) return null;

    return createPortal(
        /**
         * The wheel needs its own scroll lock, or this window cannot be scrolled at all.
         *
         * This window opens over a Mantine `Modal`, which wraps its content in
         * `react-remove-scroll`. That lock puts a non-passive `wheel` listener on
         * `document` and `preventDefault()`s every event whose target is not inside the
         * locked subtree. Portalling to `document.body` puts us outside it — so the sheet
         * rendered, clicked and edited fine while the wheel did nothing, which reads
         * exactly like a table that was clipped with no scroll left. No amount of
         * `overflow` on the body could have fixed it.
         *
         * Mounting our own lock pushes us to the top of the library's lock stack: the
         * modal's listener sees it is no longer last and bails out, and ours records the
         * wheel events that start inside this window as its own and lets them through.
         * `removeScrollBar` stays off because the modal underneath already compensated for
         * the page scrollbar; doing it twice shifts the layout sideways.
         */
        <RemoveScroll forwardProps removeScrollBar={false}>
            <div
                className={classes.backdrop}
                onClick={onClose}
                role="presentation"
            >
                {/* Stop propagation so clicks inside the window don't dismiss it. */}
                <div
                    className={`${classes.window}${wide ? ` ${classes.wide}` : ''}`}
                    onClick={(e) => e.stopPropagation()}
                    role="dialog"
                    aria-modal="true"
                    aria-label={title}
                >
                    <div className={classes.titlebar}>
                        <Group gap={8} wrap="nowrap">
                            <button
                                type="button"
                                className={classes.close}
                                onClick={onClose}
                                aria-label="Close"
                            />
                            <span className={`${classes.light} ${classes.amber}`} />
                            <span className={`${classes.light} ${classes.green}`} />
                        </Group>
                        <Stack gap={0}>
                            <Text size="sm" fw={700}>
                                {title}
                            </Text>
                            {subtitle && (
                                <Text size="xs" c="dimmed">
                                    {subtitle}
                                </Text>
                            )}
                        </Stack>
                    </div>
                    {banner && <div className={classes.banner}>{banner}</div>}
                    <div className={classes.scrollArea}>
                        {edges.top && <div className={classes.fadeTop} />}
                        <div
                            className={classes.body}
                            ref={bodyRef}
                            onScroll={measure}
                        >
                            {children}
                        </div>
                        {edges.bottom && (
                            <div className={classes.fadeBottom}>
                                <span className={classes.fadeHint}>more below ↓</span>
                            </div>
                        )}
                    </div>
                    {footer && <div className={classes.footer}>{footer}</div>}
                </div>
            </div>
        </RemoveScroll>,
        document.body,
    );
};

export default ExplainWindow;
